import { useUserReviewsInitialData } from '@/scopes/products/user-reviews/hooks';
import { useState } from 'react';
import { Icon, Skeleton, Typography, horizontalGap, tk } from '@sunwebgroup/ui-components';
import { useIntl } from '@/intl';
import { styled } from 'styled-components';
import { LegalDisclamer } from '../UserReviews/Teaser/LegalDisclamer';
import { Extended } from '../UserReviews/Result/Extended';
import { useCMSPageField } from '@/cms/fields';

const SkeletonLoader = styled(Skeleton)`
  width: 100px;
  height: ${tk('typography_body_sm_bold_size')} + ${tk('spacing_xs')};
  border-radius: ${tk('radius_extra_large')};
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  @media ${tk('mq_tablet')} {
    flex-direction: column;
    align-self: baseline;
  }
`;

const RatingWrapper = styled.button`
  display: flex;
  align-items: center;
  flex-direction: row-reverse;

  @media ${tk('mq_tablet')} {
    flex-direction: unset;
  }
`;

const ScoreLabel = styled(Typography)`
  color: ${tk('color_semantic_text_strong')};
`;

const ScoreWrapper = styled.div`
  padding: ${tk('spacing_xs')} ${tk('spacing_sm')};
  border-radius: ${tk('radius_extra_large')};
  background-color: ${tk('color_semantic_bg_subdued_hover')};
  display: flex;
  margin-right: ${tk('spacing_xs')};
  @media ${tk('mq_tablet')} {
    margin-left: ${tk('spacing_xs')};
    margin-right: unset;
  }
`;

const AmountWrapper = styled.div`
  ${horizontalGap('spacing_xs')}
  display: flex;
  justify-content: end;
  margin-top: unset;
  margin-left: ${tk('spacing_xs')};

  @media ${tk('mq_tablet')} {
    margin-left: unset;
    margin-top: ${tk('spacing_2xs')};
  }
`;

const ButtonAmount = styled.button`
  display: flex;
  align-items: center;
  text-align: end;
`;

const Amount = styled(Typography)`
  color: ${tk('color_semantic_text_strong')};
  text-decoration: underline;
`;

const InformationIcon = styled(Icon)`
  color: ${tk('color_palette_grey_900')};
`;

const ButtonInformation = styled.button`
  display: flex;
  align-items: center;
`;

export function StandaloneReviewsScore() {
  const ratingsCount = useCMSPageField('userReviews.ratingsCount');
  const ratingAverage = useCMSPageField('userReviews.ratingAverage');
  const mostRatedCategoryScore = useCMSPageField('userReviews.mostRatedCategoryScore');

  const { reviewsAmount, isFetched, isLoading } = useUserReviewsInitialData();
  const intl = useIntl();
  const [showExtendedModal, setShowExtendedModal] = useState(false);
  const [showLegalDisclaimerModal, setShowLegalDisclaimerModal] = useState(false);

  const openExtendedModal = () => {
    setShowExtendedModal(true);
  };
  const closeExtendedModal = () => {
    setShowExtendedModal(false);
  };

  const openLegalDisclaimerModal = () => {
    setShowLegalDisclaimerModal(true);
  };
  const closeLegalDisclaimerModal = () => {
    setShowLegalDisclaimerModal(false);
  };

  if (isLoading)
    return (
      <Wrapper>
        <RatingWrapper>
          <SkeletonLoader />
        </RatingWrapper>
        <AmountWrapper>
          <SkeletonLoader />
        </AmountWrapper>
      </Wrapper>
    );

  if (!ratingsCount || (isFetched && reviewsAmount < 3)) return null;

  return (
    <Wrapper>
      <RatingWrapper onClick={openExtendedModal}>
        <ScoreLabel $variant="body-sm">
          {mostRatedCategoryScore &&
            intl.formatMessage({ id: `reviews_score_bar_label_${mostRatedCategoryScore?.typeId ?? '0'}` })}
        </ScoreLabel>
        {ratingAverage && (
          <ScoreWrapper>
            <Typography $variant="body-sm-bold">{ratingAverage}</Typography>
          </ScoreWrapper>
        )}
      </RatingWrapper>
      <AmountWrapper>
        {ratingsCount && (
          <ButtonAmount onClick={openExtendedModal}>
            <Amount $variant="body-sm">
              {intl.formatMessage({ id: 'reviews_score_amount_label' }, { amount: ratingsCount })}
            </Amount>
          </ButtonAmount>
        )}
        <ButtonInformation onClick={openLegalDisclaimerModal}>
          <InformationIcon glyph="IconInformationCircle" />
        </ButtonInformation>
      </AmountWrapper>
      <LegalDisclamer closeModal={closeLegalDisclaimerModal} showModal={showLegalDisclaimerModal} />
      <Extended closeModal={closeExtendedModal} showModal={showExtendedModal} />
    </Wrapper>
  );
}
